// js/utils.js

/**
 * Creates a smooth SVG path data string (d attribute) using Catmull-Rom to Cubic Bezier conversion.
 * @param {Array<{x: number, y: number}>} pts - Array of points the curve should pass through/near.
 * @param {number} [tension=0.5] - Controls the tightness of the curve. 0 is linear, 1 is tighter.
 * @returns {string} The SVG path data string.
 */
export function catmullRom2Bezier(pts, tension = 0.5) {
  if (!pts || pts.length < 2) return "";

  let d = `M${pts[0].x},${pts[0].y}`; // Start moveTo

  // Iterate through the segments of the curve
  for (let i = 0; i < pts.length - 1; i++) {
    // Determine control points p0, p1, p2, p3 for the Catmull-Rom spline segment
    // Handle boundary conditions: if points don't exist, reuse adjacent points
    const p0 = i === 0 ? pts[i] : pts[i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = i + 2 < pts.length ? pts[i + 2] : p2; // Reuse p2 if p3 is out of bounds

    // Calculate the cubic Bezier control points (cp1, cp2) for the segment from p1 to p2
    // Formula derived from the Catmull-Rom spline definition
    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 6;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 6;

    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 6;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 6;

    // Append the cubic Bezier segment to the path data string
    d += `C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

/**
 * Helper function to generate intermediate wavy points for the curve.
 * @param {{x: number, y: number}} p1 Start point
 * @param {{x: number, y: number}} p2 End point
 * @param {number} numPoints Number of intermediate points to create
 * @param {number} amplitude The maximum distance the wave will deviate from the straight line
 * @returns {Array<{x: number, y: number}>}
 */
export function generateWavyPoints(p1, p2, numPoints = 3, amplitude = 40) {
  const intermediatePoints = [];
  const deltaX = p2.x - p1.x;
  const deltaY = p2.y - p1.y;
  const totalDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

  // Calculate a normalized perpendicular vector
  let perpX = -deltaY;
  let perpY = deltaX;
  if (totalDistance > 0) {
      const len = Math.sqrt(perpX * perpX + perpY * perpY);
      if (len > 0) { perpX /= len; perpY /= len; }
      else { perpX = 0; perpY = 0; }
  } else {
      perpX = 0; perpY = 0;
  }

  for (let i = 1; i <= numPoints; i++) {
      const fraction = i / (numPoints + 1);
      const lineX = p1.x + fraction * deltaX;
      const lineY = p1.y + fraction * deltaY;
      const currentAmplitude = amplitude * Math.sin(fraction * Math.PI); // Sine variation
      const sign = (i % 2 === 1) ? 1 : -1; // Alternate offset direction
      const offsetX = sign * currentAmplitude * perpX;
      const offsetY = sign * currentAmplitude * perpY;
      intermediatePoints.push({ x: lineX + offsetX, y: lineY + offsetY });
  }
  return intermediatePoints;
}

// NOTE: The other functions from your provided file are not needed for this specific animation,
// but are kept here in case of future use.
// --- REST OF THE FILE UNCHANGED ---